import {useAtomValue} from 'jotai';
import {memo, useEffect, useRef} from 'react';
import toast from '@/components/HotToast';
import {Franchisee} from '@/map/@types';
import {useNearbyFranchiseese} from '@/map/api/franchise';
import {nearbyStoreCategoryAtom, starredBrandsInfoAtom} from '@/map/atom';
import {MARKER_COUNT_LIMIT, MAX_VISIBLE_ZOOM_METER, MESSAGES} from '@/map/constants';
import {useAdjustMarkerCount, useMapBounds, useVisibleZoomLevel} from '@/map/hooks';
import {getOverlapedMarkers} from '@/map/utils';
import {getIntlString} from '@/utils';
import {BrandMarker} from '../../Markers';

interface IProps {
  map: naver.maps.Map;
}

export const NearbyFranchiseeLayer = (props: IProps) => {
  const categoryBrand = useAtomValue(nearbyStoreCategoryAtom);
  const starredBrand = useAtomValue(starredBrandsInfoAtom);
  const hasCategoryActive =
    categoryBrand.active && !!categoryBrand.selected?.length;
  const hasStarredActive =
    starredBrand.active && !!starredBrand.selected?.length;

  const categoryToastId = useRef<string>();
  const starredToastId = useRef<string>();
  const isVisibleLevel = useVisibleZoomLevel(
    MAX_VISIBLE_ZOOM_METER.nearby_franchise
  );

  useEffect(() => {
    if (hasCategoryActive && !isVisibleLevel) {
      categoryToastId.current = toast(
        getIntlString(MESSAGES, 'area-analytics-zoom-warning', {
          type: '주변프랜차이즈를',
        }),
        {duration: Infinity}
      );
    } else if (categoryToastId?.current) {
      toast.dismiss(categoryToastId.current);
      categoryToastId.current = undefined;
    }
  }, [isVisibleLevel, hasCategoryActive]);

  useEffect(() => {
    if (hasStarredActive && !isVisibleLevel) {
      starredToastId.current = toast(
        getIntlString(MESSAGES, 'area-analytics-zoom-warning', {
          type: '관심브랜드를',
        }),
        {duration: Infinity}
      );
    } else if (starredToastId?.current) {
      toast.dismiss(starredToastId.current);
      starredToastId.current = undefined;
    }
  }, [isVisibleLevel, hasStarredActive]);

  useEffect(() => {
    return () => {
      if (categoryToastId?.current) {
        toast.dismiss(categoryToastId.current);
        categoryToastId.current = undefined;
      }
      if (starredToastId?.current) {
        toast.dismiss(starredToastId.current);
        starredToastId.current = undefined;
      }
    };
  }, []);

  if (!isVisibleLevel) {
    return;
  }

  return <Markers {...props} />;
};

const Markers = memo(({map}: IProps) => {
  const categoryBrand = useAtomValue(nearbyStoreCategoryAtom);
  const starredBrand = useAtomValue(starredBrandsInfoAtom);
  const size =
    (categoryBrand.active ? categoryBrand.selected?.length : 0) +
    (starredBrand.active ? starredBrand.selected?.length : 0);

  const adjustedPercent = useAdjustMarkerCount(MARKER_COUNT_LIMIT * size);

  const nearBy = useMapBounds(map);

  const {data} = useNearbyFranchiseese(
    !!nearBy && !!size
      ? {
          ...nearBy,
          alias: categoryBrand.active ? categoryBrand.selected : undefined,
          franchises: starredBrand.active ? starredBrand.selected : undefined,
          limit: Math.round(MARKER_COUNT_LIMIT * size * adjustedPercent),
        }
      : undefined
  );

  if (!size || !data) {
    return null;
  }

  const markers: Franchisee[] = getOverlapedMarkers(data, 'id');

  return (
    <>
      {markers.map(
        (i) =>
          !!i.position && (
            <BrandMarker
              map={map}
              position={i.position}
              item={i}
              key={i.id + i.position.toString()}
            />
          )
      )}
    </>
  );
});
